// Data-only catalog of job-specific AI use cases: each entry becomes a
// prerendered SEO landing page at /ai/<slug> with a working runner wired to the
// existing gateway endpoints (image edit/gen, TTS, music/SFX, video), billed
// from the user's credit balance. Imported by platformPageMeta (keep this
// module free of React/icons so the meta bundle stays small) and mirrored in
// server/sitemap.go (sitemapAIUseCases) — tests/ai-usecases.test.ts enforces
// the sync.

export type UseCaseCategory =
  | 'Real Estate'
  | 'E-commerce'
  | 'Portraits'
  | 'Photo Fix'
  | 'Art Styles'
  | 'Design'
  | 'Fun'
  | 'Voice'
  | 'Music & SFX'
  | 'Video';

export type UseCaseKind = 'image-edit' | 'image-gen' | 'remove-bg' | 'tts' | 'music' | 'sfx' | 'video' | 'image-video';

export interface UseCasePreset {
  label: string;
  input: string;
}

export interface UseCaseDef {
  slug: string;
  title: string;
  /** Short card line on the /ai directory. */
  tagline: string;
  /** Meta description + landing hero paragraph. Unique per page. */
  description: string;
  category: UseCaseCategory;
  kind: UseCaseKind;
  /** Prompt template; '{input}' is replaced with the user's text (or preset). */
  prompt?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  defaultInput?: string;
  presets?: UseCasePreset[];
  /** Pin a gateway model; omit for the endpoint default / auto route. */
  model?: string;
  n?: number;
  size?: string;
  duration?: number;
  voice?: string;
  keywords: string[];
}

const RE = 'Real Estate';
const EC = 'E-commerce';
const PO = 'Portraits';
const PF = 'Photo Fix';
const AS = 'Art Styles';
const DE = 'Design';
const FU = 'Fun';
const VO = 'Voice';
const MU = 'Music & SFX';
const VI = 'Video';

export const useCases: UseCaseDef[] = [
  // --- Real Estate ---
  {
    slug: 'virtual-staging',
    title: 'Virtual Staging',
    tagline: 'Stage empty rooms in listing photos.',
    description: 'Adds furniture, rugs, art and lighting to an empty-room photo in a chosen style. No physical staging.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Virtually stage this empty room for a real estate listing: add tasteful {input} furniture, a rug, wall art and warm lighting. Keep the walls, windows, floors and room geometry exactly as they are. Photorealistic, bright, magazine-quality staging.',
    inputLabel: 'Staging style',
    defaultInput: 'modern',
    presets: [
      { label: 'Modern', input: 'modern' },
      { label: 'Scandinavian', input: 'scandinavian minimalist' },
      { label: 'Luxury', input: 'high-end luxury' },
      { label: 'Cozy farmhouse', input: 'cozy farmhouse' },
    ],
    keywords: ['virtual staging', 'real estate staging ai', 'stage empty room photo'],
  },
  {
    slug: 'room-restyle',
    title: 'AI Interior Design',
    tagline: 'Restyle a room photo in another style.',
    description: 'Replaces furniture, colors and decor in a room photo with a chosen interior style; layout stays.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Redesign this room in a {input} interior design style: update furniture, decor, textiles and color palette while keeping the room layout, windows and structure the same. Photorealistic interior photography.',
    inputLabel: 'Interior style',
    defaultInput: 'mid-century modern',
    presets: [
      { label: 'Mid-century', input: 'mid-century modern' },
      { label: 'Japandi', input: 'japandi' },
      { label: 'Industrial', input: 'industrial loft' },
      { label: 'Coastal', input: 'light coastal' },
    ],
    keywords: ['ai interior design', 'room restyle', 'redesign room photo'],
  },
  {
    slug: 'sky-replacement',
    title: 'AI Sky Replacement',
    tagline: 'Replace the sky in exterior photos.',
    description: 'Replaces a dull or blown-out sky with blue sky, sunset or clouds, and matches the scene lighting.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Replace the sky in this photo with {input}. Adjust the overall lighting and color balance of the scene so it matches the new sky naturally. Keep everything else unchanged. Photorealistic.',
    inputLabel: 'New sky',
    defaultInput: 'a clear blue sky with a few soft white clouds',
    presets: [
      { label: 'Blue sky', input: 'a clear blue sky with a few soft white clouds' },
      { label: 'Golden sunset', input: 'a warm golden-hour sunset sky' },
      { label: 'Dramatic clouds', input: 'a dramatic partly-cloudy sky with god rays' },
    ],
    keywords: ['sky replacement', 'replace sky in photo', 'real estate photo editing'],
  },
  {
    slug: 'twilight-photo',
    title: 'Day to Twilight',
    tagline: 'Convert daytime exteriors to dusk shots.',
    description: 'Turns a daytime exterior into a twilight shot: dusk sky, warm window light, landscape lighting.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Convert this daytime exterior photo to a twilight real estate shot: deep blue dusk sky, warm light glowing from the windows, subtle exterior and landscape lighting. Keep the building and composition identical. Photorealistic.',
    keywords: ['virtual twilight', 'day to dusk photo', 'twilight real estate photo'],
  },
  {
    slug: 'exterior-redesign',
    title: 'Home Exterior Redesign',
    tagline: 'Preview new paint, cladding or landscaping.',
    description: 'Previews new paint, cladding, landscaping or architectural style on a house photo. Position and surroundings stay.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Redesign the exterior of this house: {input}. Keep the building position, angle and surroundings the same. Photorealistic architectural photography.',
    inputLabel: 'What to change',
    defaultInput: 'repaint in a modern white and charcoal palette with fresh landscaping',
    presets: [
      { label: 'Modern repaint', input: 'repaint in a modern white and charcoal palette with fresh landscaping' },
      { label: 'Cedar + stone', input: 'add cedar wood cladding and natural stone accents' },
      { label: 'Lush garden', input: 'add a lush landscaped front garden with a new path' },
    ],
    keywords: ['exterior redesign ai', 'house facade visualizer', 'curb appeal ai'],
  },
  {
    slug: 'listing-photo-enhance',
    title: 'Listing Photo Enhancer',
    tagline: 'Fix exposure and white balance in listing photos.',
    description: 'Corrects exposure and white balance, brightens dark interiors, greens the lawn and sharpens detail. HDR look.',
    category: RE,
    kind: 'image-edit',
    prompt:
      'Enhance this property photo like a professional real estate editor: correct exposure and white balance, brighten dark areas, make the grass green and healthy, sharpen details, HDR look. Do not add, move or remove any objects.',
    keywords: ['real estate photo enhancement', 'brighten listing photos', 'hdr photo ai'],
  },

  // --- E-commerce ---
  {
    slug: 'product-photo-studio',
    title: 'AI Product Photos',
    tagline: 'Put product photos on a studio backdrop.',
    description: 'Places a product on a plain backdrop with soft studio lighting and a natural shadow. Product unchanged.',
    category: EC,
    kind: 'image-edit',
    prompt:
      'Turn this into a professional studio product photo: place the product on {input}, with soft studio lighting and a natural soft shadow. Keep the product itself pixel-accurate and unchanged. Commercial product photography.',
    inputLabel: 'Backdrop',
    defaultInput: 'a seamless white studio background',
    presets: [
      { label: 'White studio', input: 'a seamless white studio background' },
      { label: 'Warm beige', input: 'a warm beige seamless backdrop' },
      { label: 'Marble', input: 'a white marble surface with soft daylight' },
      { label: 'Dark premium', input: 'a dark matte premium backdrop with rim lighting' },
    ],
    keywords: ['ai product photography', 'product photo generator', 'white background product photo'],
  },
  {
    slug: 'product-scene',
    title: 'Product Scene Creator',
    tagline: 'Place a product in a described scene.',
    description: 'Places a product into a described scene — kitchen counter, beach, gym bench, forest — with matched lighting and shadows.',
    category: EC,
    kind: 'image-edit',
    prompt:
      'Place this product naturally into the following scene: {input}. Match lighting, perspective and shadows so it looks like a real photograph. Keep the product accurate and unchanged. Commercial lifestyle photography.',
    inputLabel: 'Scene',
    defaultInput: 'a sunlit wooden kitchen counter with soft morning light and out-of-focus plants',
    presets: [
      { label: 'Kitchen counter', input: 'a sunlit wooden kitchen counter with soft morning light and out-of-focus plants' },
      { label: 'Beach', input: 'a sandy beach towel scene with ocean bokeh at golden hour' },
      { label: 'Forest', input: 'a mossy forest floor with dappled sunlight' },
      { label: 'Gym', input: 'a modern gym bench with dramatic directional light' },
    ],
    keywords: ['product scene generator', 'lifestyle product photos ai', 'ai product background'],
  },
  {
    slug: 'fashion-model-photo',
    title: 'Flat-lay to Model',
    tagline: 'Show a garment on a model.',
    description: 'Turns a flat-lay or mannequin photo into an on-model shot. Garment design, color and fabric stay accurate.',
    category: EC,
    kind: 'image-edit',
    prompt:
      'Show this exact garment worn by {input}, full-body catalogue fashion photo, neutral studio background, natural pose, professional fashion photography. Keep the garment’s design, colors, fabric and details exactly accurate.',
    inputLabel: 'Model & look',
    defaultInput: 'a professional model with a natural relaxed pose',
    keywords: ['ai fashion model', 'flat lay to model', 'virtual try on clothing'],
  },
  {
    slug: 'background-remover',
    title: 'Background Remover',
    tagline: 'Remove image backgrounds, output a PNG.',
    description: 'Removes image backgrounds, including hair and fur edges, and returns a transparent PNG. Flat $0.01 per image on BiRefNet v2.',
    category: EC,
    kind: 'remove-bg',
    keywords: ['remove background', 'background remover free', 'transparent png maker'],
  },
  {
    slug: 'food-photo-enhance',
    title: 'Food Photo Enhancer',
    tagline: 'Improve dish photo color and lighting.',
    description: 'Improves lighting and color in dish photos for menus, without changing the dish or plating.',
    category: EC,
    kind: 'image-edit',
    prompt:
      'Enhance this food photo to professional food-photography quality: appetizing warm lighting, rich natural colors, shallow depth of field, crisp details. Keep the exact same dish, plating and composition — enhance only.',
    keywords: ['food photography ai', 'enhance food photos', 'menu photo editor'],
  },
  {
    slug: 'mockup-generator',
    title: 'Mockup Generator',
    tagline: 'Generate product mockup images.',
    description: 'Generates photorealistic mockups from a description: a tote bag on a cafe table, packaging on a shelf.',
    category: EC,
    kind: 'image-gen',
    prompt: 'Photorealistic product mockup: {input}. Professional commercial photography, soft studio lighting, high detail.',
    inputLabel: 'Describe the mockup',
    defaultInput: 'a plain white ceramic coffee mug on a rustic cafe table, morning light',
    keywords: ['mockup generator ai', 'product mockup maker', 'packaging mockup'],
  },

  // --- Portraits ---
  {
    slug: 'professional-headshot',
    title: 'AI Headshot Generator',
    tagline: 'Generate headshots from a selfie.',
    description: 'Turns a selfie into a headshot with business attire, studio lighting and a neutral backdrop. Face stays recognizable.',
    category: PO,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a professional corporate headshot: {input}, soft studio lighting, clean neutral background, sharp focus, professional retouching. Keep the person’s face, identity and features accurate.',
    inputLabel: 'Look',
    defaultInput: 'smart business attire',
    presets: [
      { label: 'Business', input: 'smart business attire' },
      { label: 'Business casual', input: 'business casual attire' },
      { label: 'Creative', input: 'smart casual creative-professional look' },
    ],
    keywords: ['ai headshot generator', 'professional headshot from selfie', 'linkedin photo ai'],
  },
  {
    slug: 'id-photo',
    title: 'ID Photo Maker',
    tagline: 'Make ID-style photos from a portrait.',
    description: 'Turns a portrait into a plain white background photo with even lighting and front-facing framing.',
    category: PO,
    kind: 'image-edit',
    prompt:
      'Convert this portrait into an official ID/passport style photo: plain white background, even neutral lighting, head and shoulders framed front-on, neutral expression, no shadows. Keep the person’s face and identity exactly accurate.',
    keywords: ['id photo maker', 'passport photo ai', 'white background photo'],
  },
  {
    slug: 'avatar-maker',
    title: 'AI Avatar Maker',
    tagline: 'Make profile avatars in a chosen style.',
    description: 'Turns a photo into a head-and-shoulders profile avatar: stylized digital art, cyberpunk, fantasy or flat vector.',
    category: PO,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a {input} avatar portrait, head and shoulders, striking and clean composition, suitable as a profile picture. Keep the person recognizable.',
    inputLabel: 'Avatar style',
    defaultInput: 'stylized digital painting',
    presets: [
      { label: 'Digital art', input: 'stylized digital painting' },
      { label: 'Cyberpunk', input: 'neon cyberpunk digital art' },
      { label: 'Fantasy', input: 'epic fantasy character art' },
      { label: 'Minimal vector', input: 'flat minimal vector illustration' },
    ],
    keywords: ['ai avatar maker', 'profile picture generator', 'pfp maker'],
  },
  {
    slug: 'yearbook-photo',
    title: '90s Yearbook Photo',
    tagline: 'Make 70s, 80s or 90s yearbook portraits.',
    description: 'Turns a selfie into a 70s, 80s or 90s yearbook portrait: era hairstyle, studio backdrop, film grain.',
    category: PO,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a vintage {input} school yearbook portrait: era-appropriate hairstyle and clothing, classic studio backdrop, film photo grain and color. Keep the person’s face recognizable.',
    inputLabel: 'Era',
    defaultInput: '1990s',
    presets: [
      { label: '90s', input: '1990s' },
      { label: '80s', input: '1980s' },
      { label: '70s', input: '1970s' },
    ],
    keywords: ['ai yearbook photo', '90s yearbook trend', 'retro portrait ai'],
  },
  {
    slug: 'pet-portrait',
    title: 'AI Pet Portrait',
    tagline: 'Paint a pet photo in any style.',
    description: 'Paints a pet photo as a Renaissance general, royal oil portrait, astronaut or any style you describe.',
    category: PO,
    kind: 'image-edit',
    prompt:
      'Turn this pet into {input}. Keep the pet’s face, markings and features accurate and recognizable. Rich detailed artwork, portrait composition.',
    inputLabel: 'Portrait style',
    defaultInput: 'a royal renaissance oil painting portrait wearing regal clothing',
    presets: [
      { label: 'Renaissance royal', input: 'a royal renaissance oil painting portrait wearing regal clothing' },
      { label: 'Astronaut', input: 'an astronaut portrait in a detailed spacesuit' },
      { label: 'Watercolor', input: 'a soft watercolor painting portrait' },
    ],
    keywords: ['ai pet portrait', 'dog portrait painting', 'royal pet painting'],
  },

  // --- Photo Fix ---
  {
    slug: 'restore-old-photo',
    title: 'Old Photo Restoration',
    tagline: 'Repair scratches, tears and fading.',
    description: 'Repairs scratches, tears, creases, stains and fading in scans and recovers facial detail. Original composition stays.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Restore this old damaged photograph: repair scratches, tears, creases, stains and fading, recover facial detail and sharpness, correct exposure. Keep the original people, composition and period character exactly — restoration only, no new content.',
    keywords: ['restore old photos', 'photo restoration ai', 'fix damaged photo'],
  },
  {
    slug: 'colorize-photo',
    title: 'Photo Colorizer',
    tagline: 'Add color to black and white photos.',
    description: 'Adds natural, historically plausible color to black and white photos: skin tones, fabrics, skies and landscapes.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Colorize this black and white photograph with realistic, historically plausible natural colors: accurate skin tones, fabric colors and environment. Keep every detail, person and composition identical — add color only.',
    keywords: ['colorize black and white photos', 'photo colorizer ai', 'colourise old photo'],
  },
  {
    slug: 'photo-enhance',
    title: 'AI Photo Enhancer',
    tagline: 'Sharpen and denoise photos.',
    description: 'Sharpens soft focus, reduces noise and compression artifacts, and corrects lighting, contrast and color. Composition is unchanged.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Enhance this photo: increase sharpness and fine detail, reduce noise and compression artifacts, improve lighting, contrast and color balance. Keep the content, people and composition exactly the same — enhancement only.',
    keywords: ['ai photo enhancer', 'unblur photo', 'increase photo quality'],
  },
  {
    slug: 'object-remover',
    title: 'Object Remover',
    tagline: 'Erase objects and clutter from photos.',
    description: 'Erases what you describe — photobombers, power lines, trash, watermarks — and rebuilds the background behind it.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Remove {input} from this photo and reconstruct the background behind it seamlessly and photorealistically. Change nothing else in the image.',
    inputLabel: 'What to remove',
    inputPlaceholder: 'e.g. the people in the background',
    defaultInput: 'the distracting objects in the background',
    keywords: ['remove object from photo', 'ai object remover', 'remove person from photo'],
  },
  {
    slug: 'photo-relight',
    title: 'AI Photo Relight',
    tagline: 'Change the lighting in a photo.',
    description: 'Relights a photo after the shot: midday sun to golden hour, brighter backlit faces, low-key studio light.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Relight this photo with {input}. Keep the subject, scene and composition identical — change only the lighting, shadows and color mood. Photorealistic.',
    inputLabel: 'New lighting',
    defaultInput: 'warm golden-hour sunlight',
    presets: [
      { label: 'Golden hour', input: 'warm golden-hour sunlight' },
      { label: 'Soft studio', input: 'soft diffused studio lighting' },
      { label: 'Moody', input: 'dramatic low-key moody lighting' },
    ],
    keywords: ['relight photo ai', 'fix photo lighting', 'golden hour filter'],
  },
  {
    slug: 'season-change',
    title: 'Season Changer',
    tagline: 'Change the season in a photo.',
    description: 'Switches an outdoor photo to summer, autumn, spring or snow, with foliage and light adjusted to match.',
    category: PF,
    kind: 'image-edit',
    prompt:
      'Change the season in this photo to {input}: adjust foliage, ground cover, sky and lighting to match, keeping the location, buildings and composition identical. Photorealistic.',
    inputLabel: 'Season',
    defaultInput: 'a crisp snowy winter day',
    presets: [
      { label: 'Winter snow', input: 'a crisp snowy winter day' },
      { label: 'Autumn', input: 'golden autumn with fall foliage' },
      { label: 'Spring', input: 'spring with fresh blossom' },
      { label: 'Summer', input: 'lush green summer' },
    ],
    keywords: ['season change photo', 'add snow to photo', 'autumn photo filter'],
  },

  // --- Art Styles ---
  {
    slug: 'cartoonify',
    title: 'Cartoonify Photo',
    tagline: 'Turn photos into cartoon illustrations.',
    description: 'Converts a photo into a cartoon with bold outlines, expressive features and flat color.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a {input} cartoon illustration: bold clean outlines, expressive stylization, vibrant colors. Keep the subject clearly recognizable.',
    inputLabel: 'Cartoon style',
    defaultInput: 'classic animated movie',
    presets: [
      { label: 'Animated movie', input: 'classic animated movie' },
      { label: 'Comic book', input: 'american comic book' },
      { label: 'Saturday cartoon', input: 'retro saturday-morning cartoon' },
    ],
    keywords: ['cartoonify photo', 'photo to cartoon', 'cartoon yourself'],
  },
  {
    slug: 'anime-style',
    title: 'Photo to Anime',
    tagline: 'Convert photos to anime art.',
    description: 'Converts a photo into anime art with cel shading, detailed eyes and a painterly background.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Convert this photo into beautiful anime art: cel-shaded characters with expressive detailed eyes, painterly anime background, cinematic color grading. Keep the subject and composition recognizable.',
    keywords: ['photo to anime', 'anime filter ai', 'anime art generator'],
  },
  {
    slug: 'watercolor-painting',
    title: 'Watercolor Painting',
    tagline: 'Paint photos in watercolor.',
    description: 'Turns a photo into a watercolor with soft washes, paper texture and loose brushwork.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a delicate watercolor painting: soft translucent washes, visible paper texture, loose expressive brushwork, gentle color palette. Keep the subject recognizable.',
    keywords: ['photo to watercolor', 'watercolor painting ai', 'watercolor portrait'],
  },
  {
    slug: 'oil-painting',
    title: 'Oil Painting Portrait',
    tagline: 'Render photos as oil paintings.',
    description: 'Turns a photo into a classical oil painting with impasto brushstrokes, canvas texture and old-master color.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a classical oil painting: rich impasto brushstrokes, canvas texture, dramatic museum-quality lighting, timeless color palette in the style of the old masters. Keep the subject recognizable.',
    keywords: ['photo to oil painting', 'oil painting from photo', 'ai painting generator'],
  },
  {
    slug: 'pixel-art',
    title: 'Pixel Art Maker',
    tagline: 'Make 16-bit pixel art from photos.',
    description: 'Converts a photo into 16-bit pixel art with chunky pixels and a limited palette.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Convert this image into retro 16-bit pixel art: chunky visible pixels, limited vibrant color palette, clean video-game sprite aesthetic. Keep the subject recognizable.',
    keywords: ['pixel art generator', 'photo to pixel art', '16 bit art maker'],
  },
  {
    slug: 'pencil-sketch',
    title: 'Pencil Sketch',
    tagline: 'Draw photos as pencil sketches.',
    description: 'Converts a photo into a graphite pencil sketch with fine linework and cross-hatching on textured paper.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a realistic graphite pencil sketch: fine linework, cross-hatching and soft graphite shading on textured paper. Keep the subject accurately recognizable.',
    keywords: ['photo to sketch', 'pencil drawing ai', 'sketch portrait generator'],
  },
  {
    slug: 'pop-art',
    title: 'Pop Art Generator',
    tagline: 'Make Warhol-style pop art from photos.',
    description: 'Turns a photo into pop art: high-contrast screen-print style, halftone dots and flat color blocks.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into bold pop art: high-contrast screen-print style, halftone dot texture, flat electric color blocks, thick outlines. Keep the subject recognizable.',
    keywords: ['pop art generator', 'warhol style photo', 'pop art portrait'],
  },
  {
    slug: 'cyberpunk-style',
    title: 'Cyberpunk Filter',
    tagline: 'Restyle photos as neon cyberpunk scenes.',
    description: 'Restyles a photo as a cyberpunk scene with neon teal and magenta light and holographic signage.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Restyle this image as a cinematic cyberpunk scene: neon teal and magenta lighting, holographic signage glow, rain-slick reflective surfaces, futuristic atmosphere. Keep the subject and composition recognizable.',
    keywords: ['cyberpunk filter', 'neon photo effect', 'cyberpunk art generator'],
  },
  {
    slug: 'plushify',
    title: 'Plushify',
    tagline: 'Turn a subject into a plush toy.',
    description: 'Turns a person, pet or object into a plush toy with felt texture, stitching and button eyes.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn the subject of this photo into an adorable soft plush toy: felt and fleece texture, visible stitching, cute simplified proportions, studio product photo of the plushie. Keep it clearly recognizable as the subject.',
    keywords: ['plushify photo', 'turn photo into plush toy', 'ai plushie generator'],
  },
  {
    slug: 'action-figure',
    title: 'Action Figure Maker',
    tagline: 'Make a boxed action figure.',
    description: 'Turns a photo into a collectible boxed action figure with blister packaging and accessories you describe.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn the person in this photo into a collectible boxed action figure: realistic toy figure in blister-pack packaging with {input} as accessories, product-shot lighting, retail box art. Keep the face recognizable.',
    inputLabel: 'Accessories',
    defaultInput: 'a laptop and a coffee cup',
    keywords: ['ai action figure', 'action figure generator', 'boxed figure trend'],
  },
  {
    slug: 'clay-style',
    title: 'Claymation Style',
    tagline: 'Restyle photos as claymation characters.',
    description: 'Restyles a photo as claymation: plasticine texture with fingerprints, miniature set lighting and clay proportions.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn this photo into a claymation stop-motion style scene: handcrafted plasticine clay texture with subtle fingerprints, miniature studio set lighting, charming clay character proportions. Keep the subject recognizable.',
    keywords: ['claymation filter', 'clay style ai', 'photo to clay art'],
  },
  {
    slug: 'sticker-maker',
    title: 'AI Sticker Maker',
    tagline: 'Make die-cut stickers.',
    description: 'Makes a die-cut sticker from a photo or written idea: bold outline, white border.',
    category: AS,
    kind: 'image-edit',
    prompt:
      'Turn the subject of this image into a glossy die-cut sticker: bold clean outline, thick white sticker border, vibrant simplified illustration style, plain background. Keep the subject recognizable.',
    keywords: ['ai sticker maker', 'photo to sticker', 'die cut sticker generator'],
  },

  // --- Design ---
  {
    slug: 'logo-maker',
    title: 'AI Logo Maker',
    tagline: 'Generate logo concepts from a description.',
    description: 'Generates logo concepts from a brand description: minimal marks, mascots, lettermarks or emblems.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'Professional logo design: {input}. Flat vector style, simple memorable mark, clean composition on a plain background, no photographic elements.',
    inputLabel: 'Describe your brand & logo',
    defaultInput: 'a minimal geometric fox mark for a fintech startup called Foxledger, orange and charcoal',
    keywords: ['ai logo maker', 'logo generator', 'logo design ai'],
  },
  {
    slug: 'tattoo-designer',
    title: 'AI Tattoo Designer',
    tagline: 'Generate tattoo design sheets.',
    description: 'Generates a clean tattoo design sheet from a description of style, subject and placement.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'Tattoo design: {input}. Clean tattoo flash sheet on plain background, crisp linework, professional tattoo design style.',
    inputLabel: 'Describe your tattoo',
    defaultInput: 'a fine-line hummingbird with geometric accents, forearm piece',
    presets: [
      { label: 'Fine-line', input: 'a fine-line hummingbird with geometric accents, forearm piece' },
      { label: 'Traditional', input: 'an american traditional snake and dagger with roses' },
      { label: 'Blackwork', input: 'a bold blackwork wolf with ornamental patterns' },
    ],
    keywords: ['ai tattoo generator', 'tattoo design ideas', 'custom tattoo design'],
  },
  {
    slug: 'coloring-page',
    title: 'Coloring Page Maker',
    tagline: 'Generate print-ready coloring pages.',
    description: 'Generates black and white coloring pages from any prompt: bold outlines, no shading, print-ready.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'Black and white coloring book page: {input}. Clean bold outlines only, no shading or grayscale fill, white background, print-ready line art suitable for coloring.',
    inputLabel: 'Coloring page idea',
    defaultInput: 'a friendly dragon reading a book in a cozy library',
    keywords: ['coloring page generator', 'ai coloring book', 'printable coloring pages'],
  },
  {
    slug: 'thumbnail-maker',
    title: 'Thumbnail Maker',
    tagline: 'Generate YouTube thumbnail art.',
    description: 'Generates 16:9 thumbnail art from a description of your video, with space left for title text. 1536x1024.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'YouTube thumbnail background art: {input}. Bold high-contrast composition, dramatic lighting, vivid colors, expressive and eye-catching, clear space for large title text, 16:9.',
    inputLabel: 'Describe your video',
    defaultInput: 'a developer reacting to code on a giant glowing screen, shocked expression',
    size: '1536x1024',
    keywords: ['youtube thumbnail maker', 'ai thumbnail generator', 'thumbnail background'],
  },
  {
    slug: 'poster-maker',
    title: 'AI Poster Maker',
    tagline: 'Generate poster art.',
    description: 'Generates printed-poster artwork for events, films, gigs and rooms in the style you describe. 1024x1536.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'Poster artwork: {input}. Striking composition designed as a printed poster, strong focal point, balanced negative space, high detail.',
    inputLabel: 'Describe the poster',
    defaultInput: 'a retro-futuristic travel poster for Mars, bold shapes, sunset palette',
    size: '1024x1536',
    keywords: ['ai poster maker', 'poster generator', 'event poster design'],
  },
  {
    slug: 'pattern-maker',
    title: 'Seamless Pattern Maker',
    tagline: 'Generate tileable textures and patterns.',
    description: 'Generates repeating tiles for game terrain, wallpaper, fabric and wrapping paper on the dedicated tiling model, which keeps opposite edges compatible.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'Seamless repeating tileable texture: {input}. Orthographic surface, even lighting, balanced detail distribution, no border, no frame, no text.',
    inputLabel: 'Tile or pattern idea',
    defaultInput: 'hand-painted mossy forest floor for a top-down fantasy game, readable shapes, low visual noise',
    presets: [
      { label: 'Fantasy grass', input: 'hand-painted emerald grass and tiny clover for a top-down fantasy game, readable low-noise detail' },
      { label: 'Ocean water', input: 'deep blue stylized ocean water with soft wavelets and subtle foam, top-down game terrain' },
      { label: 'Dungeon stone', input: 'ancient dark stone floor with worn cracks, faint moss and restrained fantasy detail' },
      { label: 'Botanical fabric', input: 'hand-drawn botanical leaves and wildflowers, sage green and cream, flat fabric pattern' },
    ],
    model: 'seamless-tiling',
    size: '1024x1024',
    keywords: ['seamless pattern generator', 'ai pattern maker', 'game terrain texture', 'tileable texture generator', 'fabric pattern design'],
  },
  {
    slug: 'icon-maker',
    title: 'AI Icon Generator',
    tagline: 'Generate app and UI icon art.',
    description: 'Generates 1024x1024 app icons in a chosen style — flat, glassy, pixel or 3D.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'App icon design: {input}. Single centered icon, clean silhouette, modern polished style, subtle gradient background, no text, presented like a high-end app store icon.',
    inputLabel: 'Icon idea',
    defaultInput: 'a friendly robot head, flat modern style, indigo gradient',
    size: '1024x1024',
    keywords: ['ai icon generator', 'app icon maker', 'icon design ai'],
  },
  {
    slug: 'tshirt-design',
    title: 'T-shirt Design Maker',
    tagline: 'Generate print-ready t-shirt graphics.',
    description: 'Generates a t-shirt graphic from a description: vector look, vintage badge or streetwear.',
    category: DE,
    kind: 'image-gen',
    prompt:
      'T-shirt graphic design: {input}. Bold screen-print style artwork centered on a plain background, limited color palette, crisp edges, print-ready apparel graphic, no mockup.',
    inputLabel: 'Design idea',
    defaultInput: 'a vintage badge of a mountain sunrise with the text "Keep Climbing"',
    keywords: ['t shirt design ai', 'tshirt graphic generator', 'print on demand designs'],
  },

  // --- Fun ---
  {
    slug: 'emoji-maker',
    title: 'AI Emoji Maker',
    tagline: 'Generate custom emoji icons.',
    description: 'Generates 1024x1024 emoji icons for Slack, Discord and chats from a description.',
    category: FU,
    kind: 'image-gen',
    prompt:
      'A single emoji icon: {input}. Glossy modern emoji style like iOS emoji, centered on a plain white background, simple bold shapes, expressive.',
    inputLabel: 'Emoji idea',
    defaultInput: 'a happy avocado lifting weights',
    size: '1024x1024',
    keywords: ['ai emoji maker', 'custom emoji generator', 'slack emoji maker'],
  },
  {
    slug: 'fantasy-avatar',
    title: 'Fantasy Character Maker',
    tagline: 'Generate fantasy character portraits.',
    description: 'Generates fantasy character portraits from a description of race, class, gear and mood.',
    category: FU,
    kind: 'image-gen',
    prompt:
      'Fantasy character portrait: {input}. Detailed digital painting, dramatic lighting, epic fantasy art style, head-and-torso composition.',
    inputLabel: 'Describe your character',
    defaultInput: 'an elven ranger with silver hair, forest cloak and a longbow, confident smirk',
    keywords: ['fantasy character generator', 'dnd character art', 'ai character portrait'],
  },
  {
    slug: 'storybook-illustration',
    title: 'Storybook Illustrator',
    tagline: 'Illustrate children’s book scenes.',
    description: 'Generates children’s-book illustrations from a described scene and characters in a warm picture-book style.',
    category: FU,
    kind: 'image-gen',
    prompt:
      'Children’s picture book illustration: {input}. Warm friendly art style, soft colors, gentle lighting, whimsical detail, suitable for a kids storybook page.',
    inputLabel: 'Describe the scene',
    defaultInput: 'a little fox in a yellow raincoat splashing in puddles with a duck friend',
    keywords: ['childrens book illustration ai', 'storybook art generator', 'kids book images'],
  },

  // --- Voice ---
  {
    slug: 'voiceover-generator',
    title: 'AI Voiceover Generator',
    tagline: 'Read text back as voiceover audio.',
    description: 'Reads a pasted script back as voiceover audio for videos, ads, explainers and e-learning.',
    category: VO,
    kind: 'tts',
    inputLabel: 'Script',
    defaultInput: 'This is a sample voiceover. Replace this text with your own script and download the audio.',
    keywords: ['ai voiceover generator', 'text to speech', 'voice generator online'],
  },
  {
    slug: 'story-narrator',
    title: 'Story Narrator',
    tagline: 'Narrate stories and chapters.',
    description: 'Turns stories, chapters and bedtime tales into narrated audio with pacing suited to storytelling.',
    category: VO,
    kind: 'tts',
    inputLabel: 'Story text',
    defaultInput: 'Once upon a time, in a village at the edge of a whispering forest, a small fox found a map drawn in starlight.',
    keywords: ['ai story narrator', 'audiobook voice generator', 'bedtime story voice'],
  },

  // --- Music & SFX ---
  {
    slug: 'music-maker',
    title: 'AI Music Maker',
    tagline: 'Generate music from a prompt.',
    description: 'Generates a royalty-free 30-second track from a described genre, mood and instruments. Saved to your audio library.',
    category: MU,
    kind: 'music',
    inputLabel: 'Describe the track',
    defaultInput: 'uplifting corporate indie pop with warm acoustic guitar, claps and light synths',
    duration: 30,
    keywords: ['ai music generator', 'royalty free music maker', 'text to music'],
  },
  {
    slug: 'lofi-generator',
    title: 'Lo-fi Beat Generator',
    tagline: 'Generate lo-fi hip-hop beats.',
    description: 'Generates mellow lo-fi hip-hop beats with dusty drums, warm keys and vinyl crackle, for study, streams and cafes.',
    category: MU,
    kind: 'music',
    inputLabel: 'Vibe',
    defaultInput: 'mellow lofi hip hop beat, dusty drums, warm rhodes keys, vinyl crackle, rainy night mood',
    duration: 30,
    keywords: ['lofi generator', 'ai lofi beats', 'study music generator'],
  },
  {
    slug: 'sound-effects',
    title: 'Sound Effect Generator',
    tagline: 'Generate sound effects.',
    description: 'Generates a sound effect from a description: whoosh, footsteps on gravel, sci-fi door, crowd cheer.',
    category: MU,
    kind: 'sfx',
    inputLabel: 'Describe the sound',
    defaultInput: 'a deep cinematic whoosh transition with a soft impact tail',
    duration: 8,
    keywords: ['ai sound effects', 'sfx generator', 'text to sound effect'],
  },

  // --- Video ---
  {
    slug: 'image-to-video',
    title: 'Photo Animator',
    tagline: 'Animate a photo into a clip.',
    description: 'Animates a photo into a short video clip from a described motion, on the ltx-2.3-image-to-video model.',
    category: VI,
    kind: 'image-video',
    prompt: '{input}',
    inputLabel: 'Describe the motion',
    defaultInput: 'gentle cinematic camera push-in, subtle natural motion in the scene',
    model: 'ltx-2.3-image-to-video',
    keywords: ['image to video ai', 'animate photo', 'photo to video generator'],
  },
  {
    slug: 'text-to-video',
    title: 'AI Video Generator',
    tagline: 'Generate short clips from a prompt.',
    description: 'Generates a short video clip from a described shot. The auto route picks a video model.',
    category: VI,
    kind: 'video',
    prompt: '{input}',
    inputLabel: 'Describe the video',
    defaultInput: 'aerial drone shot over turquoise coastline at golden hour, cinematic',
    model: 'auto-video',
    keywords: ['ai video generator', 'text to video', 'video clip generator'],
  },
];

export const useCaseBySlug: Record<string, UseCaseDef> = Object.fromEntries(useCases.map((u) => [u.slug, u]));

export const useCaseCategories: UseCaseCategory[] = [
  'Real Estate',
  'E-commerce',
  'Portraits',
  'Photo Fix',
  'Art Styles',
  'Design',
  'Fun',
  'Voice',
  'Music & SFX',
  'Video',
];

export function useCaseNeedsImage(kind: UseCaseKind): boolean {
  return kind === 'image-edit' || kind === 'remove-bg' || kind === 'image-video';
}

export function useCaseMeta(slug: string): { title: string; description: string } | null {
  const u = useCaseBySlug[slug];
  if (!u) return null;
  return { title: `${u.title} — free to try with credits | app.nz`, description: u.description };
}
